
import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { DiagnosticsService } from '../../services/diagnostics.service';

interface TestResult {
  id: string;
  name: string;
  icon: string;
  status: 'pending' | 'running' | 'success' | 'failed' | 'warning';
  latency?: number;
  message?: string;
}

@Component({
  selector: 'app-smoke-test-widget',
  standalone: true,
  imports: [CommonModule, DecimalPipe],
  templateUrl: './smoke-test-widget.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmokeTestWidgetComponent {
  private diagnosticsService = inject(DiagnosticsService);

  isRunning = signal(false);
  overallHealth = signal(100);
  lastRun = signal<Date | null>(null);

  tests = signal<TestResult[]>([
    { id: 'db', name: 'Database & RLS', icon: 'fa-database', status: 'pending', message: 'Ready' },
    { id: 'edge', name: 'AI Engine (Gemini)', icon: 'fa-robot', status: 'pending', message: 'Ready' },
    { id: 'realtime', name: 'Realtime Latency', icon: 'fa-bolt', status: 'pending', message: 'Ready' },
    { id: 'browser', name: 'Browser / Hardware', icon: 'fa-laptop-medical', status: 'pending', message: 'Ready' }
  ]);

  async runTests() {
    if (this.isRunning()) return;
    this.isRunning.set(true);
    this.overallHealth.set(100);
    
    // Reset states
    this.updateTest('db', { status: 'running', message: 'Querying products...', latency: undefined });
    this.updateTest('edge', { status: 'running', message: 'Testing AI...', latency: undefined });
    this.updateTest('realtime', { status: 'running', message: 'Pinging socket...', latency: undefined });
    this.updateTest('browser', { status: 'running', message: 'Checking API...', latency: undefined });

    // 1. Database & RLS Test
    try {
        const latency = await this.diagnosticsService.checkDatabaseHealth();
        this.updateTest('db', { 
            status: latency < 500 ? 'success' : 'warning', 
            latency, 
            message: latency < 500 ? 'Active & Secure' : 'Slow Response' 
        });
        if (latency >= 500) this.overallHealth.update(h => h - 10);
    } catch (e) {
        this.updateTest('db', { status: 'failed', message: 'Access Denied / Offline', latency: 0 });
        this.overallHealth.update(h => h - 40);
    }

    // 2. Edge Function / AI Test
    try {
        const result = await this.diagnosticsService.checkEdgeFunctionHealth();
        
        if (result.status === 'ok') {
            this.updateTest('edge', { 
                status: result.latency < 3000 ? 'success' : 'warning', 
                latency: result.latency, 
                message: 'Processing OK' 
            });
            if (result.latency >= 3000) this.overallHealth.update(h => h - 10);
        } else if (result.status === 'not_found') {
            this.updateTest('edge', { 
                status: 'warning', 
                message: 'Function Missing', 
                latency: 0 
            });
            this.overallHealth.update(h => h - 10);
        } else {
            this.updateTest('edge', { status: 'failed', message: result.message, latency: 0 });
            this.overallHealth.update(h => h - 20);
        }
    } catch (e) {
        this.updateTest('edge', { status: 'failed', message: 'Network Error', latency: 0 });
        this.overallHealth.update(h => h - 20);
    }

    // 3. Realtime Round-Trip Test
    try {
        const result = await this.diagnosticsService.checkRealtimeHealth();
        if (result.connected) {
            this.updateTest('realtime', { 
                status: result.latency < 200 ? 'success' : 'warning', 
                latency: result.latency,
                message: result.latency < 200 ? 'Low Latency' : 'High Latency'
            });
            if (result.latency >= 200) this.overallHealth.update(h => h - 5);
        } else {
            this.updateTest('realtime', { 
                status: 'failed', 
                message: 'Timeout / Blocked' 
            });
            this.overallHealth.update(h => h - 20);
        }
    } catch (e) {
        this.updateTest('realtime', { status: 'failed', message: 'Error' });
        this.overallHealth.update(h => h - 20);
    }

    // 4. Browser Capabilities
    const caps = await this.diagnosticsService.checkBrowserCapabilities();
    if (caps.camera && caps.canvas) {
        this.updateTest('browser', { status: 'success', message: 'Fully Supported' });
    } else if (caps.canvas) {
        this.updateTest('browser', { status: 'warning', message: 'No Camera (Kiosk Limit)' });
        this.overallHealth.update(h => h - 5);
    } else {
        this.updateTest('browser', { status: 'failed', message: 'Legacy Browser' });
        this.overallHealth.update(h => h - 10);
    }

    this.isRunning.set(false);
    this.lastRun.set(new Date());
  }

  private updateTest(id: string, updates: Partial<TestResult>) {
    this.tests.update(current => 
        current.map(t => t.id === id ? { ...t, ...updates } : t)
    );
  }

  getHealthColor(): string {
      const score = this.overallHealth();
      if (score >= 90) return 'text-emerald-400';
      if (score >= 70) return 'text-yellow-400';
      return 'text-red-500';
  }
}
